import { useCallback, useEffect, useRef, useState } from "react";

import { exhaustiveSwitch } from "replo-utils/lib/misc";

interface UseKeyboardListNavigationProps {
  itemCount: number;
  onSelect: (index: number) => void;
  isEnabled?: boolean;
}

export function useKeyboardListNavigation({
  itemCount,
  onSelect,
  isEnabled = true,
}: UseKeyboardListNavigationProps) {
  const [focusedIndex, setFocusedIndex] = useState<number>(-1);
  const itemRefs = useRef<(HTMLElement | null)[]>([]);

  // Initialize item refs array
  useEffect(() => {
    itemRefs.current = itemRefs.current.slice(0, itemCount);
  }, [itemCount]);

  // Focus the DOM element when focusedIndex changes
  useEffect(() => {
    if (
      focusedIndex >= 0 &&
      focusedIndex < itemRefs.current.length &&
      isEnabled
    ) {
      const element = itemRefs.current[focusedIndex];
      if (element) {
        element.focus();
      }
    }
  }, [focusedIndex, isEnabled]);

  // Reset focused index when item count changes or when disabled
  useEffect(() => {
    if (!isEnabled || itemCount === 0) {
      setFocusedIndex(-1);
    } else if (focusedIndex >= itemCount) {
      setFocusedIndex(itemCount - 1);
    }
  }, [itemCount, isEnabled, focusedIndex]);

  const handleKeyDown = useCallback(
    (e: React.KeyboardEvent) => {
      if (!isEnabled || itemCount === 0) {
        return;
      }

      exhaustiveSwitch({ type: e.key })({
        ArrowDown: () => {
          e.preventDefault();
          setFocusedIndex((prev) => {
            const next = prev + 1;
            return next >= itemCount ? 0 : next; // Wrap to beginning
          });
        },
        ArrowUp: () => {
          e.preventDefault();
          setFocusedIndex((prev) => {
            const next = prev - 1;
            return next < 0 ? itemCount - 1 : next; // Wrap to end
          });
        },
        Enter: () => {
          e.preventDefault();
          if (focusedIndex >= 0 && focusedIndex < itemCount) {
            onSelect(focusedIndex);
          }
        },
        default: () => {},
      });
    },
    [isEnabled, itemCount, focusedIndex, onSelect],
  );

  const focusFirstItem = useCallback(() => {
    if (isEnabled && itemCount > 0) {
      setFocusedIndex(0);
    }
  }, [isEnabled, itemCount]);

  const resetFocus = useCallback(() => {
    setFocusedIndex(-1);
  }, []);

  const getItemRef = useCallback((index: number) => {
    return (element: HTMLElement | null) => {
      itemRefs.current[index] = element;
    };
  }, []);

  return {
    focusedIndex,
    handleKeyDown,
    focusFirstItem,
    resetFocus,
    getItemRef,
  };
}
